import { Injectable, Injector } from "@angular/core";
import { environment } from "@environments/environment";
import { BulkGenerateWorkOrdersPdf } from "@interfaces/bulkGenerateWorkOrdersPdf.interface";
import { CreateQLoopDto } from "@interfaces/createQLoopDto.interface";
import { ChangeWorkOrderFirewallAssignmentDto } from "@interfaces/dto/changeWorkOrderFirewallAssignmentDto.interface";
import { ChangeWorkOrderFirewallPriorityDto } from "@interfaces/dto/changeWorkOrderFirewallPriorityDto.interface";
import { ChangeWorkOrderOperatorPriority } from "@interfaces/dto/changeWorkOrderOperatorPriority.interface";
import { GenerateOpsPlanningWorkOrdersPdf } from "@interfaces/generateOpsPlanningWorkOrdersPdf.interface";
import { WorkOrderCreationDetailsDto } from "@interfaces/workOrderCreationDetailsDto.interface";
import { Service } from "@services/service";
import { Guid } from "guid-typescript";

@Injectable()
export class WorkOrderService extends Service {
	constructor(injector: Injector) {
		super("WorkOrder", injector);
	}

	// API
	app_FirewallSetAsPicked(id: number) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/App_FirewallSetAsPicked/${id}`, {});
	}
	
	app_GetByLookup(id:Guid){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/App_GetByLookup/${id}`);
	}

	assignFirewallMachine(data: ChangeWorkOrderFirewallAssignmentDto) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/AssignFirewallMachine`, data);
	}

	assignFirewallOperator(data: ChangeWorkOrderFirewallAssignmentDto) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/AssignFirewallOperator`, data);
	}

	calculateForSalesOrderPart(id: number){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/CalculateForSalesOrderPart/${id}`);
	}

	changeCurrentStage(id: number, stage: number){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/ChangeCurrentStage/${id}/${stage}`, {});
	}
	
	changeFirewallPriority(data: ChangeWorkOrderFirewallPriorityDto){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/ChangeFirewallPriority`, data);
	}

	changeOperatorPriority(data: ChangeWorkOrderOperatorPriority){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/ChangeOperatorPriority`, data);
	}

	changeQuantity(id: number, quantity: number){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/ChangeQuantity/${id}/${quantity}`, {});
	}

	complete(id: number){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/Complete/${id}`, {});
	}

	createForSalesOrderPart(data: WorkOrderCreationDetailsDto){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/CreateForSalesOrderPart`, data);
	}

	createQLoop(data: CreateQLoopDto){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/CreateQLoop`, data);
	}
	
	forProjectId(projectId: number) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ForProjectId/${projectId}` );
	}
	
	forPurchasableItemDescription(description: string) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ForPurchasableItemDescription/${description}` );
	}

	generateAndDownloadBulkMasterLabelsPdf(data: BulkGenerateWorkOrdersPdf) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/GenerateAndDownloadBulkMasterLabelsPdf`, data, { responseType: "blob" as "json"});
	}
	
	generateAndDownloadCompleteWorkOrdersReportPdf() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/CompleteWorkOrdersReportPdf`, { responseType: "blob" as "json"} );
	}
	
	generateAndDownloadIncompleteWorkOrdersReportPdf() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/IncompleteWorkOrdersReportPdf`, { responseType: "blob" as "json"} );
	}
	
	generateAndDownloadMasterLabelPdf(id: number) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GenerateAndDownloadMasterLabelPdf/${id}`, { responseType: "blob" as "json"} );
	}

	generateAndDownloadPdf(id: number) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GenerateAndDownloadPdf/${id}`, { responseType: "blob" as "json"} );
	}

	getAllComplete(){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GetAllComplete`);
	}

	getAllGrid(){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GetAll`);
	}

	getAllIncomplete(){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GetAllIncomplete`);
	}

	getForOriginal(id: number){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ForOriginal/${id}`);
	}

	getForPart(id: number){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ForPart/${id}`);
	}

	getForSalesOrder(id: number){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ForSalesOrder/${id}`);
	}

	getStageSummaryForPart(partId: number){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/StageSummaryForPart/${partId}`);
	}
	
	opsPlanningWorkOrderSummaryPdf(parameters: GenerateOpsPlanningWorkOrdersPdf) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/OpsPlanningWorkOrderSummaryPdf`, parameters, { responseType: "blob" as "json"} );
	}

	pickList() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/PickList`, { responseType: "blob" as "json" });
	}

	recalculateEstimates(lookupId: Guid){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/RecalculateEstimates/${lookupId}`, {});
	}

	regenerate(id: number){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/Regenerate/${id}`, {});
	}

	resetProgress(id: number){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/ResetProgress/${id}`, {});
	}
	
	split(id: number){
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/Split/${id}`, {});
	}

	spotOrdersReport(){
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/SpotOrdersReport`);
	}
}